/**
 * AdminCICDPipeline — live CI/CD Pipeline Dashboard (Phase A, Item 7).
 *
 * Six sections backed by four GET endpoints under /api/admin/cicd/*:
 *   1. Pipeline status timeline (staging + main workflow runs)
 *   2. Service health (ECS / RDS / Lambda)
 *   3. Blue/Green frontend state (CloudFront)
 *   4. Mobile build history (EAS)
 *   5. Recent PRs ladder
 *   6. Activity feed (rolling)
 *
 * Polling: 60s for live data (1/2/5/6), 5min for slow-changing (3/4).
 * Graceful degradation: each section renders a "⚠ data unavailable"
 * stub when its data source fails — the page never crashes.
 *
 * The pre-existing static reference content (operating guide, blue/green
 * cheat sheet, etc.) is preserved under collapsible "Reference" panels
 * below the live sections.
 */
import React, { useEffect, useState } from 'react';
import {
  GitBranch, RefreshCw, ExternalLink, AlertTriangle,
  Clock, Server, Cloud, Smartphone, Activity, GitPullRequest,
} from 'lucide-react';
import { adminFetch } from '../utils/adminApi';
import BranchDriftWidget from './admin/BranchDriftWidget';

// ── Types (mirror routers/admin_cicd_pipeline.py response models) ────────

interface WorkflowRunSummary {
  id: number;
  name: string | null;
  head_branch: string | null;
  head_sha: string;
  head_sha_full: string;
  status: string;
  conclusion: string | null;
  event: string | null;
  created_at: string | null;
  run_started_at: string | null;
  updated_at: string | null;
  duration_seconds: number | null;
  html_url: string | null;
}

interface PRRow {
  number: number;
  title: string;
  state: string;
  merged: boolean;
  merged_at: string | null;
  merge_commit_sha: string | null;
  user: string | null;
  base: string | null;
  head: string | null;
  html_url: string;
}

interface ActivityEvent {
  kind: 'workflow_run' | 'pr_merged';
  when: string;
  text: string;
  url: string | null;
}

interface PipelineStatus {
  staging_runs: WorkflowRunSummary[];
  main_runs: WorkflowRunSummary[];
  recent_prs: PRRow[];
  activity: ActivityEvent[];
  fetched_at: string;
  cached: boolean;
  error: string | null;
}

interface AvailabilityWrap<T> {
  available: boolean;
  error?: string;
  [k: string]: any;
}

interface ServiceHealth {
  ecs: AvailabilityWrap<any> & { services?: any[] };
  rds: AvailabilityWrap<any>;
  lambda_fn: AvailabilityWrap<any>;
  fetched_at: string;
  cached: boolean;
}

interface FrontendState {
  cloudfront: AvailabilityWrap<any>;
  last_prod_deploy: WorkflowRunSummary | null;
  fetched_at: string;
  cached: boolean;
}

interface MobileBuilds {
  builds: AvailabilityWrap<any> & { builds?: any[] };
  fetched_at: string;
  cached: boolean;
}

// ── Helpers ──────────────────────────────────────────────────────────────

const REPO_RUN_BASE = 'https://github.com/Coach-Cedar/research-beta/actions/runs/';

function statusDot(status: string, conclusion: string | null): { color: string; label: string } {
  if (status !== 'completed') return { color: '#F59E0B', label: status };
  if (conclusion === 'success') return { color: '#10B981', label: 'success' };
  if (conclusion === 'failure') return { color: '#EF4444', label: 'failed' };
  if (conclusion === 'cancelled') return { color: '#6B7280', label: 'cancelled' };
  return { color: '#6B7280', label: conclusion ?? '—' };
}

function fmtDuration(seconds: number | null): string {
  if (seconds == null) return '—';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

function fmtTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

// ── Generic section primitives ───────────────────────────────────────────

const SectionCard: React.FC<{
  title: string;
  icon: React.ComponentType<{ style?: React.CSSProperties }>;
  children: React.ReactNode;
  right?: React.ReactNode;
}> = ({ title, icon: Icon, children, right }) => (
  <section style={{
    background: '#FFFFFF', borderRadius: 14, padding: 20, marginBottom: 16,
    boxShadow: '0px 1px 3px rgba(0,0,0,0.1), 0px 1px 2px rgba(0,0,0,0.06)',
  }}>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Icon style={{ width: 18, height: 18, color: '#0B1614' }} />
        <h2 style={{ fontSize: 16, fontWeight: 700, color: '#0B1614', margin: 0 }}>{title}</h2>
      </div>
      {right}
    </div>
    {children}
  </section>
);

const Unavailable: React.FC<{ error?: string }> = ({ error }) => (
  <div style={{
    padding: 14, borderRadius: 8, background: '#FFF7ED',
    border: '1px solid #FED7AA', fontSize: 12, color: '#9A3412',
    display: 'flex', alignItems: 'flex-start', gap: 8,
  }}>
    <AlertTriangle style={{ width: 14, height: 14, color: '#EA580C', flexShrink: 0, marginTop: 2 }} />
    <div>data unavailable{error ? ` — ${error}` : ''}</div>
  </div>
);

// ── Section 1 — Pipeline status timeline ────────────────────────────────

const WorkflowRunRow: React.FC<{ r: WorkflowRunSummary }> = ({ r }) => {
  const dot = statusDot(r.status, r.conclusion);
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: '12px 1fr 100px 100px 24px',
      gap: 10, alignItems: 'center', padding: '8px 0',
      borderBottom: '1px solid #F3F4F6',
    }}>
      <div style={{ width: 10, height: 10, borderRadius: '50%', background: dot.color }} />
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 13, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {r.name ?? 'workflow'} <code style={{ fontFamily: 'monospace', fontSize: 11, color: '#6B7280' }}>{r.head_sha}</code>
        </div>
        <div style={{ fontSize: 11, color: '#6B7280' }}>
          {fmtTime(r.run_started_at ?? r.created_at)} · {dot.label} · {r.event ?? ''}
        </div>
      </div>
      <div style={{ fontSize: 12, color: '#6B7280', textAlign: 'right' }}>{fmtDuration(r.duration_seconds)}</div>
      <div style={{ fontSize: 12, color: '#6B7280', textAlign: 'right' }}>
        {r.head_branch ?? ''}
      </div>
      <a href={r.html_url ?? `${REPO_RUN_BASE}${r.id}`} target="_blank" rel="noreferrer"
         style={{ color: '#42A5A5', display: 'inline-flex', alignItems: 'center' }}>
        <ExternalLink style={{ width: 14, height: 14 }} />
      </a>
    </div>
  );
};

const PipelineTimeline: React.FC<{ data: PipelineStatus | null; loading: boolean }> = ({ data, loading }) => {
  if (loading && !data) return <div style={{ color: '#9CA3AF', fontSize: 13 }}>Loading…</div>;
  if (!data) return <Unavailable error="no data" />;
  if (data.error) return <Unavailable error={data.error} />;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
      <div>
        <div style={{ fontSize: 11, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
          staging.yml — last 5 runs
        </div>
        {data.staging_runs.length === 0 ? (
          <div style={{ color: '#9CA3AF', fontSize: 12 }}>no recent runs</div>
        ) : (
          data.staging_runs.map(r => <WorkflowRunRow key={r.id} r={r} />)
        )}
      </div>
      <div>
        <div style={{ fontSize: 11, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
          ci.yml (prod) — last 5 runs
        </div>
        {data.main_runs.length === 0 ? (
          <div style={{ color: '#9CA3AF', fontSize: 12 }}>no recent runs</div>
        ) : (
          data.main_runs.map(r => <WorkflowRunRow key={r.id} r={r} />)
        )}
      </div>
    </div>
  );
};

// ── Section 2 — Service health ──────────────────────────────────────────

const ServiceHealthTable: React.FC<{ data: ServiceHealth | null; loading: boolean }> = ({ data, loading }) => {
  if (loading && !data) return <div style={{ color: '#9CA3AF', fontSize: 13 }}>Loading…</div>;
  if (!data) return <Unavailable />;

  const rows: Array<{ name: string; status: string; tasks: string; p95: string; ok: boolean; err?: string }> = [];

  if (data.ecs?.available && Array.isArray(data.ecs.services)) {
    for (const s of data.ecs.services) {
      rows.push({
        name: s.service_name ?? '?',
        status: s.status ?? '?',
        tasks: `${s.running_count}/${s.desired_count}`,
        p95: s.latency_p95_ms != null ? `${s.latency_p95_ms} ms` : '—',
        ok: s.status === 'ACTIVE' && s.running_count === s.desired_count,
      });
    }
  } else {
    rows.push({ name: 'ECS services', status: '⚠ unavailable', tasks: '—', p95: '—', ok: false, err: data.ecs?.error });
  }

  if (data.rds?.available) {
    rows.push({
      name: `RDS · ${data.rds.identifier}`,
      status: data.rds.status ?? '?',
      tasks: data.rds.multi_az ? 'Multi-AZ' : 'Single-AZ',
      p95: `${data.rds.instance_class}`,
      ok: data.rds.status === 'available',
    });
  } else {
    rows.push({ name: 'RDS coach-cedar-postgres', status: '⚠ unavailable', tasks: '—', p95: '—', ok: false, err: data.rds?.error });
  }

  if (data.lambda_fn?.available) {
    rows.push({
      name: `Lambda · ${data.lambda_fn.function_name}`,
      status: data.lambda_fn.state ?? '?',
      tasks: data.lambda_fn.runtime ?? '—',
      p95: data.lambda_fn.last_modified ?? '—',
      ok: data.lambda_fn.state === 'Active',
    });
  } else {
    rows.push({ name: 'Lambda cedar-error-digest', status: '⚠ unavailable', tasks: '—', p95: '—', ok: false, err: data.lambda_fn?.error });
  }

  return (
    <div style={{ overflow: 'hidden', borderRadius: 8, border: '1px solid #E5E7EB' }}>
      <div style={{
        display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1.5fr',
        background: '#F9FAFB', padding: '8px 12px',
        fontSize: 11, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.05em',
      }}>
        <div>Service</div><div>Status</div><div>Tasks</div><div>Detail</div>
      </div>
      {rows.map((r, i) => (
        <div key={i} title={r.err ?? ''} style={{
          display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1.5fr',
          padding: '10px 12px', fontSize: 13, color: '#111827',
          borderTop: '1px solid #F3F4F6',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{
              width: 8, height: 8, borderRadius: '50%', display: 'inline-block',
              background: r.ok ? '#10B981' : '#F59E0B',
            }} />
            {r.name}
          </div>
          <div>{r.status}</div>
          <div>{r.tasks}</div>
          <div style={{ fontSize: 12, color: '#6B7280' }}>{r.p95}</div>
        </div>
      ))}
    </div>
  );
};

// ── Section 3 — Blue/Green ──────────────────────────────────────────────

const BlueGreenPanel: React.FC<{ data: FrontendState | null; loading: boolean }> = ({ data, loading }) => {
  if (loading && !data) return <div style={{ color: '#9CA3AF', fontSize: 13 }}>Loading…</div>;
  if (!data) return <Unavailable />;
  if (!data.cloudfront?.available) return <Unavailable error={data.cloudfront?.error} />;
  const active = data.cloudfront.active_color;
  const inactive = data.cloudfront.inactive_color;
  return (
    <div>
      <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 12 }}>
        CloudFront <code style={{ fontFamily: 'monospace' }}>{data.cloudfront.distribution_id}</code>
        {' · '}status: {data.cloudfront.status}
      </div>
      <div style={{ display: 'flex', gap: 12 }}>
        <div style={{
          flex: 1, padding: 14, borderRadius: 10,
          background: active === 'blue' ? '#DBEAFE' : active === 'green' ? '#D1FAE5' : '#F3F4F6',
          border: '1px solid', borderColor: active === 'blue' ? '#93C5FD' : active === 'green' ? '#A7F3D0' : '#D1D5DB',
        }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase' }}>Active origin</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: '#111827', marginTop: 4 }}>{active ?? '—'}</div>
          <div style={{ fontSize: 11, color: '#6B7280', marginTop: 6, fontFamily: 'monospace' }}>
            {data.cloudfront.origin_domain ?? '—'}
          </div>
          {data.last_prod_deploy && (
            <div style={{ fontSize: 11, color: '#6B7280', marginTop: 6 }}>
              live since {fmtTime(data.last_prod_deploy.updated_at)} · commit{' '}
              <code style={{ fontFamily: 'monospace' }}>{data.last_prod_deploy.head_sha}</code>
            </div>
          )}
        </div>
        <div style={{
          flex: 1, padding: 14, borderRadius: 10,
          background: '#F9FAFB', border: '1px dashed #D1D5DB',
        }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase' }}>Inactive bucket</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: '#6B7280', marginTop: 4 }}>{inactive ?? '—'}</div>
          <div style={{ fontSize: 11, color: '#6B7280', marginTop: 6 }}>standby — next deploy lands here</div>
        </div>
      </div>
    </div>
  );
};

// ── Section 4 — Mobile builds ───────────────────────────────────────────

const MobileBuildsTable: React.FC<{ data: MobileBuilds | null; loading: boolean }> = ({ data, loading }) => {
  if (loading && !data) return <div style={{ color: '#9CA3AF', fontSize: 13 }}>Loading…</div>;
  if (!data) return <Unavailable />;
  if (!data.builds?.available) return <Unavailable error={data.builds?.error} />;
  const builds = data.builds.builds ?? [];
  if (builds.length === 0) return <div style={{ color: '#9CA3AF', fontSize: 13 }}>no recent builds</div>;
  return (
    <div style={{ overflow: 'hidden', borderRadius: 8, border: '1px solid #E5E7EB' }}>
      <div style={{
        display: 'grid', gridTemplateColumns: '80px 100px 130px 110px 1fr 24px',
        background: '#F9FAFB', padding: '8px 12px',
        fontSize: 11, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase',
      }}>
        <div>Platform</div><div>Version</div><div>Profile</div><div>Status</div><div>Completed</div><div></div>
      </div>
      {builds.slice(0, 8).map((b: any) => (
        <div key={b.id} style={{
          display: 'grid', gridTemplateColumns: '80px 100px 130px 110px 1fr 24px',
          padding: '10px 12px', fontSize: 13, color: '#111827',
          borderTop: '1px solid #F3F4F6',
        }}>
          <div style={{ textTransform: 'uppercase', fontSize: 11, fontWeight: 600 }}>{b.platform}</div>
          <div>{b.app_version ?? '—'} ({b.build_number ?? '—'})</div>
          <div style={{ fontSize: 12, color: '#6B7280' }}>{b.profile ?? '—'}</div>
          <div>{b.status ?? '—'}</div>
          <div style={{ fontSize: 12, color: '#6B7280' }}>{fmtTime(b.completed_at ?? b.created_at)}</div>
          <div>
            {b.build_url && (
              <a href={b.build_url} target="_blank" rel="noreferrer" style={{ color: '#42A5A5' }}>
                <ExternalLink style={{ width: 14, height: 14 }} />
              </a>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

// ── Section 5 — Recent PRs ladder ───────────────────────────────────────

const PRsLadder: React.FC<{ data: PipelineStatus | null }> = ({ data }) => {
  if (!data || data.error) return <Unavailable error={data?.error ?? 'no data'} />;
  if (data.recent_prs.length === 0) {
    return <div style={{ color: '#9CA3AF', fontSize: 13 }}>no recent merged PRs</div>;
  }

  // Identify staging-only PRs older than 2h with no follow-on main release.
  const mergedToStaging = data.recent_prs.filter(p => p.base === 'staging');
  const mergedToMain = data.recent_prs.filter(p => p.base === 'main');
  const mainMergeTimes = mergedToMain.map(p => Date.parse(p.merged_at ?? ''));
  const newestMainMerge = mainMergeTimes.length ? Math.max(...mainMergeTimes) : 0;

  return (
    <div style={{ overflow: 'hidden', borderRadius: 8, border: '1px solid #E5E7EB' }}>
      <div style={{
        display: 'grid', gridTemplateColumns: '60px 1fr 80px 140px 140px 24px',
        background: '#F9FAFB', padding: '8px 12px',
        fontSize: 11, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase',
      }}>
        <div>PR</div><div>Title</div><div>Base</div><div>Merged</div><div>Author</div><div></div>
      </div>
      {data.recent_prs.map(pr => {
        const stuck =
          pr.base === 'staging' &&
          pr.merged_at &&
          (Date.now() - Date.parse(pr.merged_at)) > 2 * 3600 * 1000 &&
          newestMainMerge < Date.parse(pr.merged_at);
        return (
          <div key={pr.number} style={{
            display: 'grid', gridTemplateColumns: '60px 1fr 80px 140px 140px 24px',
            padding: '10px 12px', fontSize: 13, color: '#111827',
            borderTop: '1px solid #F3F4F6',
            background: stuck ? '#FFF7ED' : 'transparent',
          }}>
            <div style={{ fontFamily: 'monospace', color: '#42A5A5' }}>#{pr.number}</div>
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {stuck && <AlertTriangle style={{ width: 12, height: 12, color: '#EA580C', verticalAlign: 'middle', marginRight: 4 }} />}
              {pr.title}
            </div>
            <div style={{ fontSize: 12, color: '#6B7280' }}>{pr.base}</div>
            <div style={{ fontSize: 12, color: '#6B7280' }}>{fmtTime(pr.merged_at)}</div>
            <div style={{ fontSize: 12, color: '#6B7280' }}>{pr.user ?? '—'}</div>
            <a href={pr.html_url} target="_blank" rel="noreferrer" style={{ color: '#42A5A5' }}>
              <ExternalLink style={{ width: 14, height: 14 }} />
            </a>
          </div>
        );
      })}
    </div>
  );
};

// ── Section 6 — Activity feed ───────────────────────────────────────────

const ActivityFeed: React.FC<{ data: PipelineStatus | null }> = ({ data }) => {
  if (!data || data.error) return <Unavailable error={data?.error ?? 'no data'} />;
  if (data.activity.length === 0) return <div style={{ color: '#9CA3AF', fontSize: 13 }}>no recent activity</div>;
  return (
    <div style={{ maxHeight: 360, overflowY: 'auto', borderRadius: 8, border: '1px solid #E5E7EB' }}>
      {data.activity.map((e, i) => (
        <div key={i} style={{
          display: 'flex', alignItems: 'flex-start', gap: 10,
          padding: '8px 12px', borderTop: i ? '1px solid #F3F4F6' : 'none',
          fontSize: 13,
        }}>
          <div style={{
            width: 6, height: 6, borderRadius: '50%', flexShrink: 0, marginTop: 7,
            background: e.kind === 'pr_merged' ? '#42A5A5' : '#9CA3AF',
          }} />
          <div style={{ minWidth: 90, fontSize: 11, color: '#6B7280', fontFamily: 'monospace' }}>
            {fmtTime(e.when)}
          </div>
          <div style={{ flex: 1, minWidth: 0, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {e.text}
          </div>
          {e.url && (
            <a href={e.url} target="_blank" rel="noreferrer" style={{ color: '#42A5A5', flexShrink: 0 }}>
              <ExternalLink style={{ width: 12, height: 12 }} />
            </a>
          )}
        </div>
      ))}
    </div>
  );
};

// ── Main page ──────────────────────────────────────────────────────────

export const AdminCICDPipeline: React.FC = () => {
  const [pipeline, setPipeline] = useState<PipelineStatus | null>(null);
  const [pipelineLoading, setPipelineLoading] = useState(true);

  const [services, setServices] = useState<ServiceHealth | null>(null);
  const [servicesLoading, setServicesLoading] = useState(true);

  const [frontend, setFrontend] = useState<FrontendState | null>(null);
  const [frontendLoading, setFrontendLoading] = useState(true);

  const [mobile, setMobile] = useState<MobileBuilds | null>(null);
  const [mobileLoading, setMobileLoading] = useState(true);

  const [lastRefresh, setLastRefresh] = useState<number>(Date.now());

  const fetchFast = async () => {
    try {
      const [p, s] = await Promise.allSettled([
        adminFetch<PipelineStatus>('/api/admin/cicd/pipeline-status'),
        adminFetch<ServiceHealth>('/api/admin/cicd/service-health'),
      ]);
      if (p.status === 'fulfilled') setPipeline(p.value);
      if (s.status === 'fulfilled') setServices(s.value);
    } finally {
      setPipelineLoading(false);
      setServicesLoading(false);
      setLastRefresh(Date.now());
    }
  };

  const fetchSlow = async () => {
    try {
      const [f, m] = await Promise.allSettled([
        adminFetch<FrontendState>('/api/admin/cicd/frontend-state'),
        adminFetch<MobileBuilds>('/api/admin/cicd/mobile-builds'),
      ]);
      if (f.status === 'fulfilled') setFrontend(f.value);
      if (m.status === 'fulfilled') setMobile(m.value);
    } finally {
      setFrontendLoading(false);
      setMobileLoading(false);
    }
  };

  useEffect(() => {
    fetchFast();
    fetchSlow();
    const fastId = setInterval(fetchFast, 60_000);
    const slowId = setInterval(fetchSlow, 5 * 60_000);
    return () => { clearInterval(fastId); clearInterval(slowId); };
  }, []);

  const secondsAgo = Math.max(0, Math.floor((Date.now() - lastRefresh) / 1000));

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <GitBranch style={{ width: 24, height: 24, color: '#0B1614' }} />
          <h1 style={{ fontSize: 22, fontWeight: 700, color: '#0B1614', margin: 0 }}>
            CI/CD Pipeline — live
          </h1>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 12, color: '#6B7280' }}>refreshed {secondsAgo}s ago</span>
          <button
            onClick={() => { fetchFast(); fetchSlow(); }}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '6px 12px', borderRadius: 6, border: '1px solid #D1D5DB',
              background: '#FFFFFF', cursor: 'pointer', fontSize: 13,
            }}
          >
            <RefreshCw style={{ width: 14, height: 14 }} /> Refresh
          </button>
        </div>
      </div>

      {/* Top row: drift widget */}
      <div style={{ marginBottom: 16 }}>
        <BranchDriftWidget />
      </div>

      <SectionCard title="Pipeline status timeline" icon={Activity}>
        <PipelineTimeline data={pipeline} loading={pipelineLoading} />
      </SectionCard>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <SectionCard title="Service health" icon={Server}>
          <ServiceHealthTable data={services} loading={servicesLoading} />
        </SectionCard>
        <SectionCard title="Blue/Green frontend" icon={Cloud}>
          <BlueGreenPanel data={frontend} loading={frontendLoading} />
        </SectionCard>
      </div>

      <SectionCard title="Mobile build history (EAS)" icon={Smartphone}>
        <MobileBuildsTable data={mobile} loading={mobileLoading} />
      </SectionCard>

      <SectionCard title="Recent PRs — pipeline ladder" icon={GitPullRequest}>
        <PRsLadder data={pipeline} />
      </SectionCard>

      <SectionCard title="Activity feed" icon={Clock}>
        <ActivityFeed data={pipeline} />
      </SectionCard>
    </div>
  );
};

export default AdminCICDPipeline;
